import React, { useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../api/snackbarSlice';
import { apiClient } from '../../utils/axios';
import { markPdfDataExtractionReportDownloaded } from '../../api/pdfDataExtraction/pdfDataExtractionSlice';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';
import { usePermissions } from '../../hooks/usePermissions';
import {
  CREATE_PURCHASE_CREDIT_NOTE,
  CREATE_SALE_CREDIT_NOTE,
  UPDATE_PURCHASE_CREDIT_NOTE,
  UPDATE_PURCHASE_INVOICE,
  UPDATE_SALE_CREDIT_NOTE,
  UPDATE_SALE_INVOICE,
} from '../../constants/eInvoicePermissions';
import { useAccessDeniedMessage } from '../../hooks/useAccessDeniedMessage';
import PermissionDeniedWrapper from '../../components/PermissionDeniedWrapper';

const DocumentActions = ({
  documentId,
  selectedInvoice,
  documentType,
  documentApiBaseKey,
  isPurchase,
  allowIssueCreditNote,
  allowResubmit,
  allowSendCustomPaymentLink,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { eInvoiceForm } = useTranslatedMessages();
  const { document_actions } = eInvoiceForm;
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();

  const updatePermission = isPurchase
    ? documentApiBaseKey === 'credit-notes'
      ? UPDATE_PURCHASE_CREDIT_NOTE
      : UPDATE_PURCHASE_INVOICE
    : documentApiBaseKey === 'credit-notes'
      ? UPDATE_SALE_CREDIT_NOTE
      : UPDATE_SALE_INVOICE;
  const createCreditNotePermission = isPurchase ? CREATE_PURCHASE_CREDIT_NOTE : CREATE_SALE_CREDIT_NOTE;
  const canResubmit = hasEInvoicePermission(updatePermission);
  const canRaiseCreditNote = hasEInvoicePermission(createCreditNotePermission);

  const handleDownloadXMLFile = async (documentId, options = {}) => {
    const { mlsFor = 'document', type } = options;
    dispatch(showSnackbar({ message: document_actions.downloading_xml, severity: 'info' }));

    try {
      const params = { mls_for: mlsFor };
      if (type) params.type = type;

      const response = await apiClient.get(
        `${isPurchase ? 'purchase' : 'sales'}-${documentApiBaseKey}/${documentId}/xml`,
        {
          responseType: 'text',
          headers: {
            Accept: 'application/xml',
          },
          params,
        },
      );

      const blob = new Blob([response.data], { type: 'application/xml;charset=utf-8' });
      const fallbackFileName = `${documentType}-${documentId}.xml`;
      const suggestedFileName = response.headers?.['x-suggested-filename'];
      const fileName = (typeof suggestedFileName === 'string' && suggestedFileName.trim()) || fallbackFileName;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);

      dispatch(markPdfDataExtractionReportDownloaded(true));
    } catch (error) {
      dispatch(
        showSnackbar({
          open: true,
          message: `${document_actions.error_downloading_xml}: ${error.message}`,
          severity: 'error',
        }),
      );
      dispatch(markPdfDataExtractionReportDownloaded(false));
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const onClickingDetails = () => {
    navigate(
      isPurchase
        ? `/purchases/documents/${documentApiBaseKey}/${documentId}`
        : `/sales/documents/${documentApiBaseKey}/${documentId}`,
    );
  };
  const handleUpdateResubmit = () => {
    if (!canResubmit) {
      return;
    }
    navigate(
      isPurchase
        ? `/purchases/documents/${documentApiBaseKey}/${documentId}/resubmit`
        : `/sales/documents/${documentApiBaseKey}/${documentId}/resubmit`,
    );
  };
  const handleRaiseCreditNote = () => {
    if (!canRaiseCreditNote) {
      return;
    }
    const toNavigate = isPurchase ? `/purchases/documents/credit-notes/create` : `/sales/documents/credit-notes/create`;
    navigate(toNavigate, {
      state: {
        source: 'invoice',
        invoiceData: selectedInvoice,
      },
    });
  };
  const handleSendCustomPaymentLink = async (documentId) => {
    // 1. Strictly enforce Sales Invoice update permission
    if (!hasEInvoicePermission(UPDATE_SALE_INVOICE)) {
      handleCloseMenu();
      return;
    }

    // 2. Show "Sending..." feedback loader/snackbar
    dispatch(showSnackbar({ message: 'Sending custom payment link...', severity: 'info' }));

    try {
      // 3. Trigger the POST API request strictly to the sales invoices endpoint
      const response = await apiClient.post(
        `sales-invoices/${documentId}/generate-payment-link`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      dispatch(
        showSnackbar({
          open: true,
          message: response.data?.message || 'Custom payment link sent successfully!',
          severity: 'success',
        }),
      );
      handleCloseMenu();
    } catch (error) {
      // 5. Handle error scenario
      dispatch(
        showSnackbar({
          open: true,
          message: `Error sending payment link: ${error.response?.data?.errors?.message || error.response?.data?.message || error.message}`,
          severity: 'error',
        }),
      );
    }
  };
  return (
    <>
      <IconButton id="action-icon-button" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            style: {
              boxShadow: '4px 4px 10px 0px rgba(0,0,0,0.2)',
            },
          },
        }}
      >
        <MenuItem id="details" onClick={onClickingDetails}>
          {document_actions.details}
        </MenuItem>

        <MenuItem id="download" onClick={() => handleDownloadXMLFile(documentId)}>
          {document_actions.download_xml}
        </MenuItem>
        <MenuItem id="download-tdd" onClick={() => handleDownloadXMLFile(documentId, { type: 'tdd' })}>
          {document_actions.download_tdd}
        </MenuItem>
        <MenuItem
          id="download-document-mls"
          onClick={() => handleDownloadXMLFile(documentId, { type: 'mls', mlsFor: 'document' })}
        >
          {document_actions.download_document_mls}
        </MenuItem>
        <MenuItem
          id="download-tdd-mls"
          onClick={() => handleDownloadXMLFile(documentId, { type: 'mls', mlsFor: 'tdd' })}
        >
          {document_actions.download_tdd_mls}
        </MenuItem>
        {allowResubmit && (
          <PermissionDeniedWrapper show={!canResubmit} title={accessDeniedMessage} fullWidth>
            <MenuItem id="update-resubmit" disabled={!canResubmit} onClick={handleUpdateResubmit}>
              {document_actions.fix_and_resubmit}
            </MenuItem>
          </PermissionDeniedWrapper>
        )}
        {allowIssueCreditNote && (
          <PermissionDeniedWrapper show={!canRaiseCreditNote} title={accessDeniedMessage} fullWidth>
            <MenuItem id="raise-credit-note" disabled={!canRaiseCreditNote} onClick={handleRaiseCreditNote}>
              {document_actions.raise_credit_note}
            </MenuItem>
          </PermissionDeniedWrapper>
        )}
        {allowSendCustomPaymentLink && (
          <MenuItem id="send-custom-payment-link" onClick={() => handleSendCustomPaymentLink(documentId)}>
            {document_actions.send_custom_payment_link}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default DocumentActions;
